import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Transition } from '@headlessui/react';
import { BriefcaseIcon, LoginIcon, UserIcon } from '@heroicons/react/outline';

import PublicOnlyRoute from 'components/Navigation/PublicOnlyRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import ExternalHeader from 'components/Navigation/ExternalHeader';
import InputErrorMessage from 'components/InputErrorMessage';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import { classNames } from 'utils/functions';
import { useAuth } from 'contexts/Auth';

export default function LoginPage() {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const history = useNavigate();
  const auth = useAuth();

  const password = watch('password');

  const [profile, setProfile] = useState('natural');
  const [loading, setLoading] = useState(false);

  const login = async (data: any) => {
    setLoading(true);
    let result: any = await auth.signin(
      false,
      data.ruc ? `${data.doc_number}|${data.ruc}` : data.doc_number,
      data.password
    );
    if (!result.success) {
      setLoading(false);
      return showErrorToast(result.message);
    }
    showSuccessToast(result.message);
    history(result?.data?.redirect || '/my_operations', { replace: true });
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        placeholder={`Ingrese su ${label}`}
        type={type}
        name={name}
        register={register}
        watch={watch}
        errors={errors}
        rules={rules}
        bgClass="bg-gray-100"
        {...props}
      />
    );
  };

  return (
    <PublicOnlyRoute>
      <div className="min-h-full bg-gray-100">
        <ExternalHeader />
        <div className="mx-auto flex flex-col justify-center py-8 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 ">
          <div className="mx-auto w-full max-w-md">
            <div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Ingresa a tu cuenta</h2>
              <p className="mt-2 text-sm text-gray-600">
                O{' '}
                <Link to="/signup" className="font-medium text-primary-600 hover:text-primary-500">
                  crea una nueva
                </Link>
              </p>
            </div>

            <form onSubmit={handleSubmit(login)} className="mt-8">
              <div>
                <p className="text-lg font-medium text-gray-700">¿Con qué perfil deseas operar?</p>

                <div className="mt-4 grid h-24 grid-cols-2 gap-3">
                  <div>
                    <button
                      onClick={() => setProfile('natural')}
                      className={classNames(
                        profile === 'natural'
                          ? 'border font-bold text-primary-600 shadow-md shadow-primary-500'
                          : 'border-2 font-medium text-gray-500 shadow-sm',
                        'inline-flex w-full justify-between rounded-md border-gray-300 bg-white py-4 px-4 text-left text-sm transition-all duration-300 hover:bg-gray-50'
                      )}
                    >
                      <span className="text-sm">Persona Natural</span>
                      <UserIcon
                        className={classNames(
                          profile === 'natural' ? 'h-12 w-12' : 'h-6 w-6',
                          'transition-all duration-300'
                        )}
                      />
                    </button>
                  </div>

                  <div>
                    <button
                      onClick={() => setProfile('business')}
                      className={classNames(
                        profile === 'business'
                          ? 'border font-bold text-primary-600 shadow-md shadow-primary-500'
                          : 'border-2 font-medium text-gray-500 shadow-sm',
                        'inline-flex w-full justify-between rounded-md border-gray-300 bg-white py-4 px-4 text-left text-sm transition-all duration-300 hover:bg-gray-50'
                      )}
                    >
                      <span className="text-sm">Empresa</span>
                      <BriefcaseIcon
                        className={classNames(
                          profile === 'business' ? 'h-12 w-12' : 'h-6 w-6',
                          'transition-all duration-300'
                        )}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="relative mt-8">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-gray-100 px-2 text-gray-500">Ingrese sus datos</span>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex flex-col space-y-4">
                  <Transition
                    show={profile === 'business'}
                    enter="transition-opacity duration-100"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    {buildBaseInput(
                      'N° RUC',
                      'text',
                      'ruc',
                      {
                        ...REQUIRED_RULE,
                        validate: {
                          length: (value: string) =>
                            value.length === 11 ? null : ERROR_MESSAGES.LENGTH(11),
                          pattern: (value: string) =>
                            /^[0-9]*$/.exec(value) ? null : ERROR_MESSAGES.DIGITS_ONLY,
                        },
                      },
                      {
                        inputRef: (e: string) => {
                          if (e != null) {
                            register(e, {
                              required: ERROR_MESSAGES.REQUIRED,
                            });
                          }
                        },
                      }
                    )}
                  </Transition>
                  
                  {buildBaseInput('N° Documento', 'text', 'doc_number', REQUIRED_RULE, {})}

                  <div
                    className={classNames(
                      errors['password']
                        ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500'
                        : 'border-gray-400 focus-within:border-primary-500 focus-within:ring-primary-500',
                      'relative rounded-lg border px-3 py-2 shadow-sm focus-within:ring-1'
                    )}
                  >
                    <div className="peer mt-1">
                      <input
                        id="password_input"
                        type="password"
                        autoComplete="current-password"
                        className="peer block w-full border-0 bg-gray-100 p-0 text-gray-900 placeholder-gray-500 focus:ring-0"
                        {...register('password')}
                      />
                    </div>
                    <label
                      htmlFor="password"
                      className={`${
                        password !== undefined && password?.length > 0
                          ? '-top-3 left-3 py-1 text-sm text-gray-900'
                          : 'top-3 left-2 text-gray-400 peer-focus-within:-top-3 peer-focus-within:left-3 peer-focus-within:py-1 peer-focus-within:text-sm peer-focus-within:text-gray-900'
                      } pointer-events-none absolute -mt-px rounded-md bg-gray-100 px-2 font-medium transition-all duration-150`}
                    >
                      Contraseña
                    </label>
                    <InputErrorMessage error={errors['password']} />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end">
                  <div className="text-sm">
                    <a href="/" className="font-medium text-primary-600 hover:text-primary-500">
                      ¿Olvidaste tu contraseña?
                    </a>
                  </div>
                </div>

                <div className="mt-6">
                  <SolidButton className="w-full" onClick={handleSubmit(login)} isLoading={loading}>
                    Iniciar sesión <LoginIcon className="ml-2 h-5 w-5" />
                  </SolidButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PublicOnlyRoute>
  );
}
